import {useCallback, useMemo} from 'react';

import {useAppSelector} from '../../helpers/reduxHelpers';
import {ApiChannel, parseChannel} from '../../models/api/channelModel';
import {fetchApiAsync, fetchAsync} from '../../services/http/http';
import {makeGetChannel} from '../../store/data/channels/channelsSelectors';
import {
  SettingsInvalidateQueriesOptions,
  useSettingsInvalidateQueries,
  useSettingsQuery,
  UseSettingsQueryOptions,
} from '../helpers/useSettingsQuery';

/*
 * Consts.
 */

const channelQueryKeyFactory = {
  id: (channelId: number) => ['channelSettings', {id: channelId}],
};

/*
 * Fetchers.
 */

export function useFetchChannel(channelId: number, options?: UseSettingsQueryOptions) {
  const getChannel = useMemo(() => makeGetChannel(() => channelId), [channelId]);
  const channelFromStore = useAppSelector(getChannel);

  const {data, asyncStatus} = useSettingsQuery(
    channelQueryKeyFactory.id(channelId),
    async ({company, cancelToken}) => {
      if (channelFromStore) {
        return channelFromStore;
      }

      return fetchApiAsync(ApiChannel, `${company.url}/channels/${channelId}`, {cancelToken});
    },
    options,
  );

  return {channel: data, status: asyncStatus};
}

export function useFetchChannels(inboxId: number, options?: UseSettingsQueryOptions) {
  const {data, asyncStatus} = useSettingsQuery(
    ['channelsForInbox', {inboxId}],
    async ({company, cancelToken}) => {
      const response = await fetchAsync(`${company.url}/inboxes/${inboxId}/channels`, {cancelToken});
      const channels = Array.from(response.channels).map((c: any) => parseChannel(c));

      return channels || [];
    },
    options,
  );

  return {channels: data || [], status: asyncStatus};
}

export function useInvalidateChannel() {
  const invalidate = useSettingsInvalidateQueries();

  return useCallback(
    (channelId: number, options?: SettingsInvalidateQueriesOptions) => {
      invalidate(channelQueryKeyFactory.id(channelId), options);
    },
    [invalidate],
  );
}
